import Collections from '../../utils/collections';
import BaseDocument from '../../Model/BaseDocument';
import { DifficultyLevel } from '../../Model/Program';

/**
 * Activity levels of users.
 */
const ActivityLevel = {
  SEDENTARY: 'SEDENTARY',
  MODERATE: 'MODERATE',
  ACTIVE: 'ACTIVE',
};

/**
 * Weights experience levels as display text.
 */
const WeightsExperienceText = {
  [DifficultyLevel.BEGINNER]: 'Beginner',
  [DifficultyLevel.INTERMEDIATE]: 'Intermediate',
  [DifficultyLevel.ADVANCED]: 'Advanced',
};

const KnownWeightsExperiences = Object.values(DifficultyLevel);

/*
 * The legacy Paperform post-payment form importer in system2-scripts
 * (src/userProfiles/conversion/formAnswerMapping.js) recorded the most advanced answer - "I have
 * trained for more than two years and feel confident in the gym" - as 'EXPERIENCED' rather than
 * 'ADVANCED'. No migration was ever run, so profiles it created still carry the value and it is
 * normalized on read here.
 */
const LEGACY_EXPERIENCED = 'EXPERIENCED';

/**
 * Class representing a user's activity profile.
 *
 * @class UserActivityProfile
 * @extends BaseDocument
 */
class UserActivityProfile extends BaseDocument {
  constructor(id, opts) {
    super(`${Collections.USER_ACTIVITY_PROFILE}/${id}`, opts);
  }

  /**
   * Get the user's level of workout activity within a week.
   * - **'NONE'** - 0 per week
   * - **'SOME'** - 1-3x per week
   * - **'OFTEN'** - 3+ per week
   * @returns {string}
   */
  get currentWorkoutDays() {
    return this.data.currentWorkoutDays || '';
  }

  /**
   * Get the user's days available to workout.
   * @returns {string}
   */
  get daysAvailableToWorkout() {
    return this.data.daysAvailableToWorkout || '';
  }

  /**
   * Get the activity level of the user.
   * @returns {string}
   */
  get activityLevel() {
    return this.data.activityLevel || '';
  }

  /**
   * Get the user's preferred workout days.
   * Values are numbers 0–6 (0 = Monday, 6 = Sunday).
   * @returns {number[]}
   */
  get preferredWorkoutDays() {
    return this.data.preferredWorkoutDays ?? [];
  }

  /**
   * Get the user's preferred workout location.
   * @returns {'HOME'|'GYM'|null}
   */
  get workoutLocation() {
    return this.data.workoutLocation ?? null;
  }

  /**
   * Get the user's weights training experience level, as answered in the post-payment form.
   * - **'BEGINNER'** - new to weights training
   * - **'INTERMEDIATE'** - has trained for some time and knows the basics
   * - **'ADVANCED'** - has trained for more than two years and feels confident in the gym
   *
   * The legacy 'EXPERIENCED' value is normalized to 'ADVANCED', and a value outside the known
   * levels is reported as absent, so callers only ever see a level they can render.
   * @returns {'BEGINNER'|'INTERMEDIATE'|'ADVANCED'|null}
   */
  get weightsExperience() {
    const storedLevel = this.data.weightsExperience === LEGACY_EXPERIENCED
      ? DifficultyLevel.ADVANCED
      : this.data.weightsExperience;
    return KnownWeightsExperiences.includes(storedLevel) ? storedLevel : null;
  }

  /**
   * Get the last updated timestamp.
   * The value is stored as milliseconds since the Unix epoch.
   * @returns {number}
   */
  get lastUpdated() {
    return this.data.lastUpdated;
  }

  /**
   * Update workout preference fields on this document, creating it if it doesn't exist.
   * @param {object} data - Partial preference fields to update.
   * @param {string} updatedBy - User ID of the person making the update.
   */
  async updatePreferences(data, updatedBy) {
    await this.updateFields({
      ...data,
      lastUpdated: Date.now(),
      lastUpdatedBy: updatedBy,
    });
  }

  /**
   * Get a user activity profile by the user's id.
   * @param {string} id - Id of the activity profile (which is the same as the user's id).
   * @returns {Promise<UserActivityProfile|void>} The activity profile document or null if it doesn't exist.
   */
  static async getById(id) {
    const activityProfileDoc = new UserActivityProfile(id);
    await activityProfileDoc.init();
    return activityProfileDoc.exists ? activityProfileDoc : null;
  }
}

export default UserActivityProfile;
export {
  ActivityLevel,
  WeightsExperienceText,
};
